import { useEffect, useState } from "react";
import { Row, Card, CardBody } from "reactstrap";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";

import { getMcapEngagementSummary, getMcapScreenViews } from "api/api.service";
import { getCached, setCached, mcapCacheKey } from "utils/mcapCache";
import KpiTile from "../components/KpiTile";
import ClassroomRollup from "../components/ClassroomRollup";
import typography from "../typography";

const GRAPH_COLORS = { teal: "#06402b", gridBorder: "#f6f6f6" };
const panelVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] } } };

export default function Engagement({ filters, filterBar }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [screenViews, setScreenViews] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("engagement", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setData(cached.summary || {});
      setScreenViews(cached.screenViews || []);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    Promise.all([getMcapEngagementSummary(filters), getMcapScreenViews(filters)])
      .then(([summaryRes, screenViewsRes]) => {
        if (cancelled) return;
        setData(summaryRes || {});
        setScreenViews(screenViewsRes?.screens || []);
        setCached(cacheKey, { summary: summaryRes, screenViews: screenViewsRes?.screens || [] });
      })
      .catch(() => {
        if (!cancelled) {
          setData({});
          setScreenViews([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const daily = data.dailyActiveUsers || [];
  const retention = data.retention || {};
  const retentionCategories = Object.keys(retention).map((k) => k.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()));
  const retentionValues = Object.values(retention);

  return (
    <div className="p-4 mt-3">
      {filterBar}
      <Row>
        <KpiTile index={0} label={typography.APP_OPENS} value={data.appOpens} loading={loading} colorClass="primary-gradient-bg" />
        <KpiTile index={1} label={typography.ACTIVE_USERS} value={data.activeUsers} loading={loading} colorClass="bg-orange" />
        <KpiTile index={2} label={typography.SESSION_COUNT} value={data.sessionCount} loading={loading} colorClass="bg-blue" />
        <KpiTile
          index={3}
          label={typography.AVG_SESSION_DURATION}
          value={data.avgSessionDurationSeconds ? Math.round(data.avgSessionDurationSeconds / 60) : 0}
          suffix=" min"
          loading={loading}
          colorClass="bg-purple"
        />
        <KpiTile index={4} label={typography.SCREEN_VIEW_COUNT} value={data.screenViewCount} loading={loading} colorClass="primary-gradient-bg" />
      </Row>

      <motion.div initial="hidden" animate="visible" variants={panelVariants}>
        <Card className="mt-3">
          <CardBody>
            <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.DAILY_ACTIVE_USERS}</h5>
            <ReactApexChart
              type="area"
              height={280}
              series={[{ name: typography.ACTIVE_USERS, data: daily.map((d) => d.activeUsers) }]}
              options={{
                chart: { toolbar: { show: false } },
                colors: [GRAPH_COLORS.teal],
                stroke: { curve: "smooth", width: 2 },
                dataLabels: { enabled: false },
                fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
                xaxis: {
                  categories: daily.map((d) => d.date),
                  labels: { style: { fontSize: "11px", fontFamily: "Poppins" } },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: { min: 0 },
                grid: { borderColor: GRAPH_COLORS.gridBorder },
              }}
            />
          </CardBody>
        </Card>
      </motion.div>

      <motion.div initial="hidden" animate="visible" variants={panelVariants}>
        <Card className="mt-3">
          <CardBody>
            <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.RETENTION_BY_WEEK}</h5>
            <ReactApexChart
              type="bar"
              height={280}
              series={[{ name: "Retained Users", data: retentionValues }]}
              options={{
                chart: { toolbar: { show: false } },
                plotOptions: { bar: { borderRadius: 4, columnWidth: "30%" } },
                colors: [GRAPH_COLORS.teal],
                dataLabels: { enabled: false },
                xaxis: {
                  categories: retentionCategories,
                  labels: { style: { fontSize: "12px", fontFamily: "Poppins" } },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: { min: 0, max: 100, labels: { formatter: (v) => `${Math.round(v)}%` } },
                grid: { borderColor: GRAPH_COLORS.gridBorder },
                tooltip: { y: { formatter: (v) => `${v}%` } },
              }}
            />
          </CardBody>
        </Card>
      </motion.div>

      <motion.div initial="hidden" animate="visible" variants={panelVariants}>
        <Card className="mt-3">
          <CardBody>
            <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.FEATURE_USAGE}</h5>
            <ReactApexChart
              type="bar"
              height={Math.max(220, screenViews.length * 34)}
              series={[{ name: "Views", data: screenViews.map((s) => s.viewCount) }]}
              options={{
                chart: { toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "55%" } },
                colors: [GRAPH_COLORS.teal],
                dataLabels: { enabled: false },
                xaxis: {
                  categories: screenViews.map((s) => s.screenName),
                  labels: { style: { fontSize: "11px", fontFamily: "Poppins" } },
                },
                grid: { borderColor: GRAPH_COLORS.gridBorder },
              }}
            />
          </CardBody>
        </Card>
      </motion.div>

      <ClassroomRollup sectionId={filters.sectionId} />
    </div>
  );
}
